import React, { useState } from 'react';
import { TrendingUp, AlertTriangle, Clock, Zap, ArrowRight } from 'lucide-react';
import ShareImageGenerator from './ShareImageGenerator';
import IndustryBreakdown from './IndustryBreakdown';
import RegionalInfrastructureMap from './RegionalInfrastructureMap';

const McKinseyTimeline = () => {
  const [activeTab, setActiveTab] = useState('timeline');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-purple-900 to-red-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="h-4 w-4" />
              2017 → 2023 → 2025
            </div>
            <h1 className="text-5xl font-bold mb-6">
              The Acceleration Nobody Saw Coming
            </h1>
            <p className="text-2xl text-blue-100 mb-8">
              How McKinsey's automation assessments changed in just 2 years
            </p>
            <div className="flex items-center justify-center gap-12 text-center">
              <div>
                <div className="text-5xl font-bold mb-2">30%</div>
                <div className="text-blue-200">McKinsey 2023</div>
                <div className="text-sm text-blue-300">by 2030</div>
              </div>
              <ArrowRight className="h-12 w-12 text-yellow-400" />
              <div>
                <div className="text-6xl font-bold mb-2 text-red-400">57%</div>
                <div className="text-blue-200">McKinsey 2025</div>
                <div className="text-sm text-red-300">automatable NOW</div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-4 justify-center mt-12">
            <button
              onClick={() => setActiveTab('timeline')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'timeline' 
                  ? 'bg-white text-blue-900 shadow-lg' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setActiveTab('industry')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'industry' 
                  ? 'bg-white text-blue-900 shadow-lg' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              By Industry
            </button>
            <button
              onClick={() => setActiveTab('regional')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                activeTab === 'regional' 
                  ? 'bg-white text-blue-900 shadow-lg' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              By Region
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Share Image Generator - Always visible at top */}
        <div className="mb-12">
          <ShareImageGenerator />
        </div>

        {/* Tab Content */}
        {activeTab === 'timeline' && <TimelineContent />}
        {activeTab === 'industry' && <IndustryBreakdown />}
        {activeTab === 'regional' && <RegionalInfrastructureMap />}
      </div>
    </div>
  );
};

// Timeline Content Component
const TimelineContent = () => {
  const timelineData = [
    {
      year: 2017,
      source: 'McKinsey "Jobs Lost, Jobs Gained"',
      headline: '50% of work activities theoretically automatable',
      subtext: 'But only 5% of jobs could be fully automated',
      timeline: '10-20 year horizon',
      color: 'blue',
      key_stat: '50%',
      reality: 'Theoretical potential, pre-AI agents'
    },
    {
      year: 2023,
      source: 'McKinsey "Generative AI and Future of Work"',
      headline: '30% of work hours by 2030',
      subtext: '12 million occupational transitions needed in U.S.',
      timeline: '7 years from publication',
      color: 'yellow',
      key_stat: '30%',
      reality: 'First generative AI assessment, still conservative'
    },
    {
      year: 2025,
      source: 'McKinsey "Agents, Robots, and Us"',
      headline: '57% of U.S. work hours automatable NOW',
      subtext: '40% of jobs in "highly automatable" category',
      timeline: 'Current technology capability',
      color: 'red',
      key_stat: '57%',
      reality: 'AI agents + intelligent robots = massive acceleration'
    }
  ];

  const accelerationMetrics = [
    {
      metric: 'Automation Potential',
      y2017: '50%',
      y2023: '30%',
      y2025: '57%',
      change: '+90% in 2 years'
    },
    {
      metric: 'Timeline',
      y2017: '10-20 years',
      y2023: 'By 2030',
      y2025: 'NOW',
      change: 'Collapsed 7 years'
    },
    {
      metric: 'Highly Automatable Jobs',
      y2017: '5%',
      y2023: 'Not specified',
      y2025: '40%',
      change: '8x increase'
    },
    {
      metric: 'Economic Value (U.S.)',
      y2017: 'Not quantified',
      y2023: 'Not specified',
      y2025: '$2.9 trillion',
      change: 'First quantification'
    }
  ];

  const whatChanged = [
    {
      title: 'AI Agents Became Autonomous',
      before: 'Copilots requiring human guidance',
      after: 'Multi-step autonomous process completion',
      impact: 'Can handle entire workflows, not just tasks'
    },
    {
      title: 'Robots Got Intelligent',
      before: 'Specialized, structured environments only',
      after: 'General-purpose with spatial reasoning',
      impact: 'Can work in unstructured physical spaces'
    },
    {
      title: 'Context Windows Exploded',
      before: 'Few pages of text (2023 GPT-4)',
      after: 'Entire codebases, 200+ page documents',
      impact: 'Can understand whole systems, not fragments'
    },
    {
      title: 'Workflow Transformation',
      before: 'Task automation (copy-paste, data entry)',
      after: 'End-to-end process redesign',
      impact: 'Companies eliminating entire job categories'
    }
  ];

  return (
    <>
      {/* Timeline Cards */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-center mb-12">The Timeline</h2>
        <div className="space-y-8">
          {timelineData.map((item, idx) => (
            <div key={item.year} className="relative">
              {idx < timelineData.length - 1 && (
                <div className="absolute left-8 top-full h-8 w-1 bg-gradient-to-b from-gray-300 to-transparent" />
              )}
              
              <div className={`bg-white rounded-xl shadow-lg border-l-8 border-${item.color}-500 p-8 hover:shadow-xl transition-all`}>
                <div className="flex items-start gap-6">
                  <div className={`flex-shrink-0 w-20 h-20 rounded-full bg-${item.color}-100 flex items-center justify-center`}>
                    <div className="text-center">
                      <div className={`text-2xl font-bold text-${item.color}-700`}>{item.year}</div>
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-2">{item.source}</div>
                    <h3 className="text-2xl font-bold mb-3">{item.headline}</h3>
                    <p className="text-gray-700 mb-4">{item.subtext}</p>
                    
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div className={`bg-${item.color}-50 rounded-lg p-4`}>
                        <div className="text-sm text-gray-600 mb-1">Key Stat</div>
                        <div className={`text-3xl font-bold text-${item.color}-700`}>{item.key_stat}</div>
                      </div>
                      <div className={`bg-${item.color}-50 rounded-lg p-4`}>
                        <div className="text-sm text-gray-600 mb-1">Timeline</div>
                        <div className={`text-lg font-semibold text-${item.color}-700`}>{item.timeline}</div>
                      </div>
                      <div className={`bg-${item.color}-50 rounded-lg p-4`}>
                        <div className="text-sm text-gray-600 mb-1">Context</div>
                        <div className="text-sm text-gray-700">{item.reality}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Acceleration Metrics Table */}
      <div className="mb-16 bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-3xl font-bold mb-8 text-center">The Numbers</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-4 px-4 font-bold">Metric</th>
                <th className="text-center py-4 px-4 font-bold">2017</th>
                <th className="text-center py-4 px-4 font-bold">2023</th>
                <th className="text-center py-4 px-4 font-bold">2025</th>
                <th className="text-right py-4 px-4 font-bold">Change</th>
              </tr>
            </thead>
            <tbody>
              {accelerationMetrics.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-4 px-4 font-semibold">{row.metric}</td>
                  <td className="text-center py-4 px-4 text-gray-600">{row.y2017}</td>
                  <td className="text-center py-4 px-4 text-gray-600">{row.y2023}</td>
                  <td className="text-center py-4 px-4">
                    <span className="text-red-600 font-bold">{row.y2025}</span>
                  </td>
                  <td className="text-right py-4 px-4">
                    <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {row.change}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* What Changed */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-center mb-4">What Changed Between 2023 and 2025?</h2>
        <p className="text-center text-gray-600 mb-12 text-lg">
          Four technological breakthroughs compressed a decade of progress into 24 months
        </p>
        
        <div className="grid md:grid-cols-2 gap-6">
          {whatChanged.map((item, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-purple-500">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Zap className="h-6 w-6 text-purple-600" />
                {item.title}
              </h3>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Before (2023):</div>
                  <div className="text-gray-700">{item.before}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">After (2025):</div>
                  <div className="text-gray-900 font-semibold">{item.after}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 border-l-4 border-purple-500">
                  <div className="text-sm font-medium text-purple-900">
                    Impact: {item.impact}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* The Meta-Lesson */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-12 text-white mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <AlertTriangle className="h-16 w-16 mx-auto mb-6 text-yellow-300" />
          <h2 className="text-4xl font-bold mb-6">The Meta-Lesson</h2>
          <p className="text-2xl mb-8 text-red-50">
            "By the time you hear about automation risk in a McKinsey report, 
            leading companies have already been implementing for 1-2 years."
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl font-bold mb-2">2 years</div>
              <div className="text-red-100">McKinsey research cycle</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl font-bold mb-2">18-24 mo</div>
              <div className="text-red-100">Corporate implementation lag</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="text-4xl font-bold mb-2">7 days</div>
              <div className="text-red-100">PivotIntel update cycle</div>
            </div>
          </div>
          <p className="text-xl text-red-100 mb-6">
            The research doesn't predict the future—it documents the present with a 12-18 month lag.
          </p>
        </div>
      </div>

      {/* Sources */}
      <div className="mt-12 bg-gray-100 rounded-lg p-6">
        <h3 className="font-bold mb-4">Sources</h3>
        <div className="space-y-2 text-sm text-gray-700">
          <div>
            <strong>2017:</strong> McKinsey Global Institute, "Jobs Lost, Jobs Gained" 
            <a href="https://www.mckinsey.com/featured-insights/future-of-work/jobs-lost-jobs-gained-what-the-future-of-work-will-mean-for-jobs-skills-and-wages" className="text-blue-600 hover:underline ml-2">Read report →</a>
          </div>
          <div>
            <strong>2023:</strong> McKinsey Global Institute, "Generative AI and the Future of Work in America" 
            <a href="https://www.mckinsey.com/mgi/our-research/generative-ai-and-the-future-of-work-in-america" className="text-blue-600 hover:underline ml-2">Read report →</a>
          </div>
          <div>
            <strong>2025:</strong> McKinsey Global Institute, "Agents, Robots, and Us" 
            <a href="https://www.mckinsey.com/mgi/our-research/agents-robots-and-us-skill-partnerships-in-the-age-of-ai" className="text-blue-600 hover:underline ml-2">Read report →</a>
          </div>
          <div>
            <strong>Analysis:</strong> PivotIntel, "McKinsey's November 2025 Bombshell" 
            <a href="https://theopenrecord.org/2025/11/27/mckinseys-november-2025-bombshell-57-of-work-hours-already-automatable/" className="text-blue-600 hover:underline ml-2">Read analysis →</a>
          </div>
        </div>
      </div>
    </>
  );
};

export default McKinseyTimeline;
